import { Fragment } from 'react'
import PropTypes from 'prop-types'
import Layout from 'components/Layout'
import WordpressSEO from 'components/WordpressSEO'
import Blocks from 'components/Blocks'
import SliderCover from 'components/SliderCover/dynamic'

import s from './highlight.module.css'
import StateInitializer from '../../../components/StateInitializer'
import ArticlesRelated from '../../../components/ArticlesRelated'
import getGlobalSettings from '../../../helpers/getGlobalSettings'
import getSites from '../../../helpers/getSites'
import HIGHLIGHT_QUERY from '../../../graphql/highlight.query'
import classNames from 'classnames'
import getSitesForStaticPaths from '../../../helpers/getSitesForStaticPaths'
import HIGHLIGHTS_QUERY from '../../../graphql/highlights.query'
import { createApolloClient } from '../../../lib/createApolloClient'
import * as Sentry from '@sentry/nextjs'

function Highlight({
  globalSettings,
  sites,
  siteSlug,
  site = '',
  highlight = null,
  slug = '',
}) {
  if (!highlight) return null

  const { title, acfHighlightCover, acfHighlightSettings, acfBlocks } =
    highlight

  const {
    models,
    globalEventTag,
    globalBrandTag,
    globalCampaignTag,
    globalCategoriesTag,
    globalPhotographerTag,
    globalPublicationTag,
    globalStylistTag,
  } = acfHighlightSettings

  const linkedModel = models?.nodes?.[0]

  const setSidebarItems = [
    globalEventTag?.nodes?.[0],
    globalBrandTag?.nodes?.[0],
    globalCampaignTag?.nodes?.[0],
    globalCategoriesTag?.nodes?.[0],
    globalPhotographerTag?.nodes?.[0],
    globalPublicationTag?.nodes?.[0],
    globalStylistTag?.nodes?.[0],
  ]

  const relatedPosts = linkedModel?.relatedPosts

  const setCovers = [
    {
      acfBlocks,
      acfHighlightCover: {
        shortDescription: acfHighlightCover?.shortDescription,
        covers: acfHighlightCover?.covers,
      },
      acfHighlightSettings,
      slug,
    },
  ]

  const setSidebarItem = (label, name) => {
    return (
      <>
        <h3 className={s.label} dangerouslySetInnerHTML={{ __html: label }} />
        <h4 className={s.name} dangerouslySetInnerHTML={{ __html: name }} />
      </>
    )
  }

  return (
    <StateInitializer
      globalSettings={globalSettings}
      sites={sites}
      siteSlug={siteSlug}
    >
      <Layout globalSettings={globalSettings}>
        <WordpressSEO
          url={`/${site}/highlight/${slug}`}
          title={highlight.title}
          seo={highlight.seo}
        />
        <SliderCover covers={setCovers} noLink />
        <div className={s.container}>
          <div className={s.blocks}>
            {acfBlocks && <Blocks postType="Highlight">{acfBlocks}</Blocks>}
          </div>
          <div className={s.sidebar}>
            <h3
              className={classNames(s.label, s.labelMobile)}
              dangerouslySetInnerHTML={{ __html: 'Credits' }}
            />
            {linkedModel &&
              setSidebarItem(linkedModel.__typename, linkedModel.title)}
            {title && setSidebarItem('Title', title)}
            {setSidebarItems.map((item) => {
              if (!item) return null
              return (
                <Fragment key={item.name}>
                  {setSidebarItem(item.__typename, item.name)}
                </Fragment>
              )
            })}
          </div>
        </div>
        <ArticlesRelated articles={relatedPosts} />
      </Layout>
    </StateInitializer>
  )
}

Highlight.propTypes = {
  globalSettings: PropTypes.object.isRequired,
  sites: PropTypes.array.isRequired,
  siteSlug: PropTypes.string.isRequired,
  site: PropTypes.string, // TODO remove this prop
  slug: PropTypes.string,
  highlight: PropTypes.object,
}

export default Highlight

export async function getStaticProps({ params }) {
  const { site, slug } = params

  Sentry.addBreadcrumb({
    message: `getStaticProps for Highlight page called - site: ${site}, slug: ${slug}`,
    category: 'getStaticProps',
    level: 'info',
    data: { params },
  })

  const apolloClient = createApolloClient()

  const [globalSettings, sites, { data, error }] = await Promise.all([
    getGlobalSettings(site),
    getSites(),
    apolloClient.query({
      query: HIGHLIGHT_QUERY,
      context: { site },
      variables: { slug },
    }),
  ])

  if (error) {
    Sentry.captureException(new Error(`Error loading Article props - ${error}`))
    console.error(`Error loading Highlight props - ${error}`)
  }

  return {
    props: {
      ...params,
      highlight: data.highlightBy,
      globalSettings,
      sites,
      siteSlug: site,
    },
    revalidate: 60 * 60, // Revalidate every hour
  }
}

export async function getStaticPaths() {
  const paths = []

  await getSitesForStaticPaths(
    ({ site }) => ({
      query: HIGHLIGHTS_QUERY,
      context: { site },
    }),
    (data, { site }) =>
      data.highlights.nodes.map((n) => {
        paths.push({
          params: {
            site,
            slug: n.slug,
          },
        })
      })
  )

  return {
    paths,
    fallback: false,
  }
}
